import React, { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Search, UserCheck } from "lucide-react";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllEmployeesNoPageNoFilter } from "../../../api/hrm/employees/employeeInfo";
import {
  exportUserAccess,
  getUserAccessEmployees,
  getUserAccessSummary,
} from "../../../api/settings/userAccess";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { ACTION_LABELS, MODULE_CATEGORIES } from "../../../config/featureGroups";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Chi nhánh • Tên nhân viên • Bộ phận • Mã nhân viên */
function formatEmployeeOptionLabel(e) {
  const name = e.employee_name || e.name || "";
  return [e.branch_name, name, e.department_name, e.code]
    .filter(Boolean)
    .join(" • ");
}

export default function UserAccessAuditPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    getUserAccessEmployees()
      .then((rows) => {
        const items = Array.isArray(rows) ? rows : [];
        setEmployees(
          items.map((e) => ({
            value: e.id,
            label: formatEmployeeOptionLabel(e),
          }))
        );
      })
      .catch(() => {
        // Fallback: chỉ NV đang active
        getAllEmployeesNoPageNoFilter().then((res) => {
          const items = (res?.items || res || []).filter((e) => e.is_active !== false);
          setEmployees(
            items.map((e) => ({
              value: e.id,
              label: formatEmployeeOptionLabel(e),
            }))
          );
        });
      });
  }, []);

  const loadSummary = useCallback(async (id) => {
    if (!id) {
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getUserAccessSummary(id);
      setSummary(data);
    } catch {
      showErrorToast("Không tải được quyền nhân viên");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(employeeId);
  }, [employeeId, loadSummary]);

  const handleExport = async (format) => {
    if (!employeeId) return;
    setExporting(format);
    try {
      const blob = await exportUserAccess(employeeId, format);
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const code = summary?.employee?.code;
      const slug =
        code && String(code).trim() && String(code).toLowerCase() !== "null"
          ? String(code).trim().replace(/[^\w\-]+/g, "_")
          : employeeId.slice(0, 8);
      downloadBlob(blob, `Phieu_phan_quyen_${slug}.${ext}`);
    } catch {
      showErrorToast("Xuất file thất bại");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <UserCheck className="text-red-600" size={22} />
          Kiểm tra quyền tài khoản
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Xem ma trận quyền theo nhóm chức năng và xuất PDF/Excel để audit.
        </p>
      </div>

      <div className="bg-white border rounded-2xl p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-1">
              <Search size={12} /> Chọn nhân viên
            </label>
            <Select
              styles={customSelectStyles}
              options={employees}
              value={employees.find((e) => e.value === employeeId) || null}
              onChange={(opt) => setEmployeeId(opt?.value || "")}
              placeholder="Chi nhánh, tên, bộ phận hoặc mã nhân viên..."
              isClearable
            />
          </div>
          {employeeId && (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!!exporting}
                onClick={() => handleExport("xlsx")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-bold hover:bg-green-50 text-green-800 border-green-200"
              >
                <FileSpreadsheet size={16} />
                {exporting === "xlsx" ? "Đang xuất..." : "Excel"}
              </button>
              <button
                type="button"
                disabled={!!exporting}
                onClick={() => handleExport("pdf")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-bold hover:bg-red-50 text-red-800 border-red-200"
              >
                <FileText size={16} />
                {exporting === "pdf" ? "Đang xuất..." : "PDF"}
              </button>
            </div>
          )}
        </div>

        {loading && (
          <p className="text-sm text-gray-400 text-center py-8">Đang tải...</p>
        )}

        {!loading && summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Info label="Mã NV" value={summary.employee.code} />
              <Info label="Họ tên" value={summary.employee.name} />
              <Info label="Chi nhánh" value={summary.employee.branch_name} />
              <Info label="Bộ phận" value={summary.employee.department_name} />
              <Info
                label="Quyền sử dụng"
                value={
                  summary.primaryRole?.usage_role_label ||
                  summary.primaryRole?.role_name ||
                  "—"
                }
              />
              <Info label="Phạm vi mặc định" value={summary.scopeLabel} />
            </div>

            {summary.presetComparison &&
              (summary.presetComparison.missing?.length > 0 ||
                summary.presetComparison.extra?.length > 0) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-bold mb-1">So với preset đề xuất</p>
                  {summary.presetComparison.missing?.length > 0 && (
                    <p>
                      Thiếu:{" "}
                      {summary.presetComparison.missing
                        .map((x) => `${x.group}/${x.action}`)
                        .join(", ")}
                    </p>
                  )}
                  {summary.presetComparison.extra?.length > 0 && (
                    <p>
                      Thừa:{" "}
                      {summary.presetComparison.extra
                        .map((x) => `${x.group}/${x.action}`)
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}

            <div className="space-y-4">
              {MODULE_CATEGORIES.map((cat) => {
                const rows = (summary.matrix || []).filter(
                  (row) =>
                    row.category === cat.key &&
                    row.actions.some((a) => a.granted || a.status === "missing")
                );
                if (!rows.length) return null;
                return (
                  <div key={cat.key} className="overflow-x-auto border rounded-xl">
                    <div className="px-3 py-2 bg-gray-50 border-b text-xs font-bold uppercase text-gray-600">
                      {cat.label}
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-white text-xs uppercase text-gray-400">
                        <tr>
                          <th className="px-3 py-2 text-left">Chức năng</th>
                          <th className="px-3 py-2 text-left whitespace-nowrap">Phạm vi</th>
                          {["C", "R", "U", "D", "E", "A", "I"].map((a) => (
                            <th key={a} className="px-2 py-2 text-center w-10">
                              {a}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.groupKey} className="border-t border-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{row.labelVi}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                                {row.scopeLabel || "—"}
                              </span>
                            </td>
                            {["C", "R", "U", "D", "E", "A", "I"].map((action) => {
                              const cell = row.actions.find((x) => x.action === action);
                              if (!cell) {
                                return (
                                  <td key={action} className="px-2 py-2 text-center text-gray-200">
                                    ·
                                  </td>
                                );
                              }
                              const cls = cell.granted
                                ? cell.status === "extra"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                                : cell.status === "missing"
                                  ? "bg-amber-50 text-amber-600 ring-1 ring-amber-200"
                                  : "";
                              return (
                                <td key={action} className="px-2 py-2 text-center">
                                  <span
                                    className={`inline-block w-6 h-6 leading-6 rounded text-xs font-bold ${cls}`}
                                    title={ACTION_LABELS[action]}
                                  >
                                    {cell.granted ? "✓" : cell.status === "missing" ? "!" : ""}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
              {!(summary.matrix || []).some((row) =>
                row.actions.some((a) => a.granted || a.status === "missing")
              ) && (
                <p className="text-center text-amber-700 bg-amber-50 border border-amber-100 rounded-xl py-6 text-sm">
                  Nhân viên này chưa được gán quyền sử dụng hoặc chưa có quyền chức năng nào.
                  Gán quyền tại <strong>Cài đặt → Gán quyền nhân viên</strong> hoặc trong hồ sơ nhân viên.
                </p>
              )}
            </div>
          </>
        )}

        {!loading && !employeeId && (
          <div className="text-center py-12 px-4">
            <UserCheck className="mx-auto text-gray-200 mb-3" size={48} />
            <p className="text-sm font-medium text-gray-600">
              Chọn một nhân viên ở trên để xem ma trận quyền
            </p>
            <p className="text-xs text-gray-400 mt-2 max-w-md mx-auto leading-relaxed">
              Trang này dùng để kiểm tra và xuất audit quyền tài khoản — không phải danh sách tất cả nhân viên.
              Sau khi chọn, bạn sẽ thấy quyền sử dụng, phạm vi dữ liệu và bảng chi tiết theo nhóm chức năng.
            </p>
          </div>
        )}

        {!loading && employeeId && !summary && (
          <p className="text-center text-gray-400 py-8">Không có dữ liệu</p>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2">
      <p className="text-[10px] uppercase font-bold text-gray-400">{label}</p>
      <p className="font-medium text-gray-800 truncate">{value || "—"}</p>
    </div>
  );
}
